import { Injectable } from '@angular/core';
import { interval, Observable } from 'rxjs';
import { debounce } from 'rxjs/operators';
import { GetAccessOnAsset, PermissionsApi } from '../api/permissions.api';
import { PermissionsState } from '../state/permissions.state';
import { User } from '../models/user.model';
import { UpdateAccessOnAsset } from '../models/updateAccessOnAsset.model';
import { GrantAccess } from '../models/grantAccess.model';
import { DeleteAccess } from '../models/deleteAccess.model';
import { ToastrWrapperService } from '../../../services/toastr-wrapper.service';

@Injectable({
    providedIn: 'root',
})

export class PermissionsFacade {

    constructor(
        private permissionApi: PermissionsApi,
        private permissionsState: PermissionsState,
        private toastr: ToastrWrapperService) { }

    // defaults
    loadUsersAndUserGroups(permissionGroupType: string, workspaceId: string, assetId: string): void {
        const pageNumber = 1;
        this.permissionsState.setDefaults();
        this.updatePermissionTypeState(permissionGroupType);
        this.updatePermissionsState(permissionGroupType);
        this.setDefaultAccessState(permissionGroupType, workspaceId, assetId, pageNumber);
    }

    loadUsersAndUserGroupsDashboardGroup(permissionGroupType: string, workspaceId: string, assetId: string): void {
        const pageNumber = 1;
        this.permissionsState.setDefaults();
        this.updatePermissionTypeState(permissionGroupType);
        this.updatePermissionsState(permissionGroupType);
        this.setDefaultAccessStateDashbboardGroup(permissionGroupType, workspaceId, assetId, pageNumber);
    }

    setDefaultAccessState(permissionGroupType: string, workspaceId: string, assetId: string, pageNumber: number): void {
        this.permissionApi.getWorkspaceRoles(workspaceId).subscribe((roles: any) => {
            this.permissionsState.updateDefaultWorkspaceRoles(permissionGroupType, roles.data);
            this.updateUsersPaginateState(permissionGroupType, workspaceId, assetId, pageNumber);
        },
            () => {
                window.alert('خطا در دریافت نقش‌های فضای کاری');
            });
    }

    setDefaultAccessStateDashbboardGroup(permissionGroupType: string,
        workspaceId: string, assetId: string, pageNumber: number): void {
        this.permissionApi.getWorkspaceRoles(workspaceId).subscribe((roles: any) => {
            this.permissionsState.updateDefaultWorkspaceRoles(permissionGroupType, roles.data);
            this.updateUsersDashboardGroup(permissionGroupType, workspaceId, assetId, pageNumber);
        },
            () => {
                window.alert('خطا در دریافت نقش‌های فضای کاری');
            });
    }

    loadForNewAccessDashboardWithDebounce(body: GrantAccess, debounceTime): void {
        this.permissionApi.getUsersNewAccess(body)
            .pipe(debounce(() => interval(debounceTime)))
            .subscribe(
                (res) => {
                    this.permissionsState.updateNewAccessDashboard(res.data);
                },
                () => { },
                () => { },
            );
    }

    loadForNewAccessDataSourceWithDebounce(body: GrantAccess, debounceTime): void {
        this.permissionApi.getUsersNewAccess(body)
            .pipe(debounce(() => interval(debounceTime)))
            .subscribe(
                (res) => {
                    this.permissionsState.updateNewAccessDataSource(res.data);
                },
                () => { },
                () => { },
            );
    }

    loadForNewAccessDashboardGroupWithDebounce(body: GrantAccess): void {
        this.permissionApi.getUsersNewAccess(body)
            .subscribe(
                (res) => {
                    this.permissionsState.updateNewAccessDashboardGroup(res.data);
                },
                () => { },
                () => { },
            );
    }

    // getters
    getUsers$(): Observable<any[]> {
        return this.permissionsState.getUsers$();
    }

    getDefaultAccess$(): Observable<any> {
        return this.permissionsState.getWorkspaceRoles$();
    }

    getPermissionsGroupType$(): Observable<any> {
        return this.permissionsState.getPermissionsGroupType$();
    }

    getTotalPages$(): Observable<any> {
        return this.permissionsState.getTotalPages$();
    }

    getPageNumber$(): Observable<any> {
        return this.permissionsState.getPageNumber$();
    }

    getNewAccessDashboard$(): Observable<any> {
        return this.permissionsState.getNewAccessDashboard$();
    }

    getNewAccessDataSource$(): Observable<any> {
        return this.permissionsState.getNewAccessDataSource$();
    }

    getNewAccessDashboardGroup$(): Observable<any> {
        return this.permissionsState.getNewAccessDashboardGroup$();
    }

    // updates

    updatePermissionTypeState(permissionGroupType: string): void {
        this.permissionsState.updatePermissionType(permissionGroupType);
    }

    updateUsersPaginateState(assetType: string, workspaceId: string, assetId: string, pageNumber: number): void {
        let body: GetAccessOnAsset;
        this.permissionsState.getPageNumber$().subscribe((num: number) => {
            pageNumber = num;
        });
        if (pageNumber === 1) {
            body = {
                workspaceId,
                assetId,
                assetType,
                pageNumber,
                pageSize: 5,
                needCount: true,
            };
        } else {
            body = {
                workspaceId,
                assetId,
                assetType,
                pageNumber,
                pageSize: 5,
            };
        }
        this.permissionApi.getUsersAndUserGroups(body)
            .subscribe((res: any) => {
                const users: User[] = res.data.pageData;
                this.permissionsState.updateUsers(users);
                if (pageNumber === 1) {
                    this.permissionsState.updateTotalPages(res.data.totalPages);
                }
                this.permissionsState.updatePageNumber(pageNumber);
            },
                () => {
                    window.alert('خطا در دریافت اطلاعات کاربران');
                });
    }

    updateUsersDashboardGroup(assetType: string, workspaceId: string, assetId: string, pageNumber: number): void {
        let body: GetAccessOnAsset;
        this.permissionsState.getPageNumber$().subscribe((num: number) => {
            pageNumber = num;
        });
        if (pageNumber === 1) {
            body = {
                workspaceId,
                assetId,
                assetType,
                pageNumber,
                pageSize: 5,
                needCount: true,
            };
        } else {
            body = {
                workspaceId,
                assetId,
                assetType,
                pageNumber,
                pageSize: 5,
            };
        }
        this.permissionApi.getUsersAndUserGroups(body)
            .subscribe((res: any) => {
                const users: User[] = res.data.pageData;
                this.permissionsState.updateUsersDashboardGroup(users);
                if (pageNumber === 1) {
                    this.permissionsState.updateTotalPages(res.data.totalPages);
                }
                this.permissionsState.updatePageNumber(pageNumber);
            },
                () => {
                    window.alert('خطا در دریافت اطلاعات کاربران');
                });
    }

    updateUsers(body: UpdateAccessOnAsset): void {
        this.permissionApi.updateDashboard(body).subscribe(
            () => { },
            () => {
                window.alert('خطادر بروزرسانی داشبورد');
            },
        );
    }

    updatePermissionsState(permissionGroupType: string): void {
      this.permissionsState.updatePermissions(permissionGroupType);
    }

    updateUserPermissions(roleId: string, userIndex: number, permissions: any, isExist: boolean): void {
        this.permissionsState.updateUserPermissions(roleId, userIndex, permissions, isExist);
    }

    updateDashboard(accessOnAsset: UpdateAccessOnAsset): void {
        this.permissionApi.updateDashboard(accessOnAsset).subscribe(
            () => {
                if (accessOnAsset.access.isNewAccess) {
                    this.setDefaultAccessState('Dashboard', accessOnAsset.workspaceId, accessOnAsset.assetId,1);
                }
            },
            (err) => {
                if (err.error.statusCode === 2) {
                    this.toastr.showError('کاربر یا گروه کاربری در این داشبورد دسترسی دارد');
                } else {
                    this.toastr.showError('عملیات بروزرسانی دسترسی ناموفق بود');
                    if (!accessOnAsset.access.isNewAccess) {
                      this.permissionsState.rollbackUsersPermissions();
                  }
                }


            });
    }

    updateDataSource(body: UpdateAccessOnAsset): void {
        this.permissionApi.updateDataSource(body).subscribe(
            () => {
                if (body.access.isNewAccess) {
                    this.setDefaultAccessState('DataSource', body.workspaceId, body.assetId,1);
                }
            },
            (err) => {
                if (err.error.statusCode === 2) {
                    this.toastr.showError('کاربر یا گروه کاربری در این داشبورد دسترسی دارد.');
                } else {
                    this.toastr.showError('عملیات بروزرسانی دسترسی ناموفق بود.');
                    this.permissionsState.rollbackUsersPermissions();
                }


            });
    }

    updateDashboardGroup(body: UpdateAccessOnAsset): void {
        this.permissionApi.updateDashboardGroup(body).subscribe(
            () => {
                if (body.access.isNewAccess) {
                  this.setDefaultAccessStateDashbboardGroup('DashboardGroup', body.workspaceId, body.assetId,1);
                }
            },
            (err) => {
                if (err.error.statusCode === 2) {
                    this.toastr.showError(err.error.message);
                } else {
                    this.toastr.showError('عملیات بروزرسانی دسترسی ناموفق بود.');
                }
                this.permissionsState.rollbackUsersPermissions();
            });
    }

    updateChart(body: UpdateAccessOnAsset): void {
      this.permissionApi.updateChart(body).subscribe(
        () => {
          this.permissionsState.setIsChartPermissionsUpdated();
          if (body.access.isNewAccess) {
            this.loadUsersAndUserGroups('Chart', body.workspaceId, body.assetId);
          }
        },
        (err) => {
          if (err.error.statusCode === 2) {
            this.toastr.showError('کاربر یا گروه کاربری در این داشبورد دسترسی دارد.');
          } else {
            this.toastr.showError('عملیات بروزرسانی دسترسی ناموفق بود.');
            this.permissionsState.rollbackUsersPermissions();
          }


        });
    }

    updateIsChartPermissionsUpdated(isUpdated: boolean) {
        this.permissionsState.updateIsChartPermissionsUpdated(isUpdated);
    }

    getIsChartPermissionsUpdated$(): Observable<boolean> {
        return this.permissionsState.getIsChartPermissionsUpdated$();
    }

    updatePageNumber(pageNumber: number): void {
        this.permissionsState.updatePageNumber(pageNumber);
    }


    deleteUserAccess(body: DeleteAccess): void {
        const numberOfUsers = this.permissionsState.deleleUserAccess(body);
        this.permissionApi.deleteUserAccess(body).subscribe(
            () => {
                if(numberOfUsers === 0) {
                    if(body.assetType !== 'DashboardGroup') {
                        this.loadUsersAndUserGroups(body.assetType, body.workspaceId, body.assetId);
                    } else {
                        this.loadUsersAndUserGroupsDashboardGroup(body.assetType, body.workspaceId, body.assetId);
                    }
                }
            },
            () => {
                this.permissionsState.rollbackUsersPermissions();
            },
            () => {},
        );
    }
}
